import React, { useState } from "react";
import styled from "styled-components";
import { types, Repeater } from "react-bricks/frontend";
import { color } from "styles/colours";
import { mobile } from "components/templates/Breakpoints";
import {
  getVerticalPadding,
  VerticalPadding,
  VerticalPaddingWrapper,
  verticalPadSideProp,
} from "../utils/verticalPadding";
import { fonts } from "styles/fonts";

export interface TextWithListItemProps {
  reverse: boolean;
  paddingVertical?: VerticalPadding;
  backgroundColor: types.IColor;
  accordionTitle: string;
  accordionBullets: boolean;
  aboveTheFold: { props: { text: string } }[];
  belowTheFold: { props: { text: string } }[];
}

export const TextWithList: types.Brick<TextWithListItemProps> = ({
  reverse = false,
  paddingVertical,
  backgroundColor,
  accordionTitle,
  accordionBullets,
  aboveTheFold,
  belowTheFold,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const aboveTheFoldTexts = aboveTheFold.map((item) => item.props.text);
  const belowTheFoldTexts = belowTheFold.map((item) => item.props.text);

  return (
    <VerticalPaddingWrapper paddingVertical={paddingVertical}>
      <Container reverse={reverse} backgroundColor={backgroundColor?.color}>
        <TextContainer>
          <Repeater propName="copyBlock" />
        </TextContainer>

        <ListContainer>
          <Card>
            <CardMain>
              <Strong>{accordionTitle}</Strong>
              <ul>
                {aboveTheFoldTexts.map((item, index) =>
                  accordionBullets ? (
                    <LI key={`above-fold-${index}`}>
                      <span>&#8226;</span> {item}
                    </LI>
                  ) : (
                    <LI key={`above-fold-${index}`}>{item}</LI>
                  ),
                )}
              </ul>
              <UL isOpen={isOpen} length={belowTheFoldTexts.length}>
                {belowTheFoldTexts.map((item, index) =>
                  accordionBullets ? (
                    <LI key={`below-fold-${index}`}>
                      <span>&#8226;</span> {item}
                    </LI>
                  ) : (
                    <LI key={`below-fold-${index}`}>{item}</LI>
                  ),
                )}
              </UL>
            </CardMain>
            <CardLink onClick={() => setIsOpen(!isOpen)}>
              <div>{isOpen ? "Close full list" : "See full list"}</div>
              <div>{isOpen ? <MinusIcon /> : <PlusIcon />}</div>
            </CardLink>
          </Card>
        </ListContainer>
      </Container>
    </VerticalPaddingWrapper>
  );
};

TextWithList.schema = {
  name: "text-with-list",
  label: "Text With List",
  previewImageUrl: "/static/bricks/text-with-list.png",
  getDefaultProps: () => ({
    verticalPadding: "s",
    backgroundColor: { color: color.white },
    accordionTitle: "Common Conditions",
    accordionBullets: true,
    aboveTheFold: [
      {
        type: "list-item",
        props: { text: "Headache" },
      },
      {
        type: "list-item",
        props: { text: "Allergies" },
      },
    ],
    belowTheFold: [
      {
        type: "list-item",
        props: { text: "Back pain" },
      },
      {
        type: "list-item",
        props: { text: "Flu symptoms" },
      },
    ],
  }),
  repeaterItems: [
    {
      name: "copyBlock",
      itemType: "text-with-heading-block",
      itemLabel: "Copy",
      min: 1,
      max: 1,
    },
    {
      name: "aboveTheFold",
      itemType: "list-item",
      itemLabel: "Above-the-fold Item",
    },
    {
      name: "belowTheFold",
      itemType: "list-item",
      itemLabel: "Below-the-fold Item",
    },
  ],
  sideEditProps: [
    {
      name: "reverse",
      label: "Reverse layout",
      type: types.SideEditPropType.Boolean,
      shouldRefreshStyles: true,
    },
    {
      name: "backgroundColor",
      label: "Background Color",
      type: types.SideEditPropType.Select,
      shouldRefreshStyles: true,
      selectOptions: {
        display: types.OptionsDisplay.Color,
        options: [
          { value: { color: color.white }, label: "White" },
          { value: { color: color.B210 }, label: "Gray" },
        ],
      },
    },
    verticalPadSideProp,
    {
      name: "accordionTitle",
      label: "Accordion Title",
      type: types.SideEditPropType.Text,
    },
    {
      name: "accordionBullets",
      label: "Use Bullet Points",
      type: types.SideEditPropType.Boolean,
    },
  ],
};

const ZIG_ZAG_BREAKPOINT = `@media only screen and (max-width: 76rem)`;

const Container = styled.div<{
  reverse: boolean;
  backgroundColor: string;
}>`
  display: flex;
  justify-content: center;
  gap: 4em;
  flex-direction: ${(p) => (p.reverse ? "row-reverse" : "row")};
  background-color: ${(p) => p.backgroundColor};

  ${ZIG_ZAG_BREAKPOINT} {
    flex-direction: column;
    gap: 3rem;
    align-items: center;

    padding-left: 2rem;
    padding-right: 2rem;
  }
`;

const TextContainer = styled.div`
  width: 30rem;
  display: flex;
  align-items: center;

  ${ZIG_ZAG_BREAKPOINT} {
    width: 34rem;
  }

  ${mobile} {
    width: 100%;
  }
`;

const ListContainer = styled.div`
  width: 34rem;

  img {
    width: 100%;
    height: auto;
    display: block;
  }

  @media only screen and (max-width: 36rem) {
    width: 100%;
  }
`;

const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none">
    <path
      d="M16 29.333c7.364 0 13.333-5.97 13.333-13.333 0-7.364-5.97-13.334-13.333-13.334C8.636 2.666 2.666 8.636 2.666 16S8.636 29.333 16 29.333zM24 16H8m8-8v16"
      stroke="#E30D76"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MinusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none">
    <path
      d="M16 29.3a13.3 13.3 0 100-26.6 13.3 13.3 0 000 26.6zM24 16H8"
      stroke="#E30D76"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Card = styled.div`
  margin-bottom: 3rem;
  box-sizing: border-box;
  box-shadow: 0.5rem 0.5rem 0 #fff7cc;
  border-radius: 0.5rem;

  transition: 200ms ease-out;

  &:hover {
    transform: translateY(-1rem);
    box-shadow: 1rem 1rem 0 #fff7cc;
  }
`;

const CardMain = styled.div`
  padding: 2rem;
  background: #fffbe5;
  border: 0.125rem solid #ffef99;
  border-radius: 0.5rem 0.5rem 0 0;
  width: 34rem;

  @media only screen and (max-width: 36rem) {
    width: 100%;
  }
`;

const Strong = styled.h3`
  display: inline-block;
  font-family: ${fonts.body};
  font-weight: bold;
  color: #640038;
  line-height: 2rem;
  letter-spacing: 0.075rem;
  margin: 0 0 1rem 0;
  font-size: 1.17rem;

  ::-moz-selection {
    background: ${color.P20};
  }
  ::selection {
    background: ${color.P20};
  }
`;

const UL = styled.ul<{ isOpen?: boolean; length?: number }>`
  transition: 400ms ease-out;
  height: auto;
  max-height: ${(p) => (p.isOpen && p.length ? `${p.length * 3}rem` : "0")};
  overflow: hidden;
`;

const LI = styled.li`
  display: block;
  font-weight: 400;
  color: #5c5757;
  line-height: 2rem;
  letter-spacing: 0.075rem;

  ::-moz-selection {
    background: ${color.P20};
  }
  ::selection {
    background: ${color.P20};
  }
`;

const CardLink = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 1.5rem 2rem;
  background: #fff7cc;
  border: 0.125rem solid #ffef99;
  border-top: none;
  border-radius: 0 0 0.5rem 0.5rem;
  font-weight: bold;
  color: #e30d76;
  line-height: 2rem;
  letter-spacing: 0.075rem;
  font-size: 1rem;
  transition: 200ms ease-out;
  height: 2rem;

  &:hover {
    background: #fff3b2;
  }

  div {
    ::-moz-selection {
      background: ${color.P20};
    }
    ::selection {
      background: ${color.P20};
    }
  }
`;
